// src/pages/customer/ClientAppointment.jsx

import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase/config';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  collectionGroup,
  getDocs
} from 'firebase/firestore';
import styled from 'styled-components';
import { FaVideo, FaClock, FaPaperPlane, FaCalendarAlt, FaExternalLinkAlt, FaUser, FaComments } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const MainTitle = styled.h1`
  color: #291759;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
  }
`;

const SectionTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: #291759;
  font-size: 1.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #291759;
  font-weight: 600;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1.25rem;
  border-radius: 12px;
  border: 2px solid #e1e8ed;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: #6c63ff;
    box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.1);
  }

  &::placeholder {
    color: #a0a0a0;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #291759, #6c63ff);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(108, 99, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const AppointmentsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const AppointmentCard = styled.div`
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border: 1px solid #e1e8ed;
  padding: 2rem;
  border-radius: 16px;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch(props.status) {
      case 'pending':
        return `
          background: linear-gradient(135deg, #ffeaa7, #fdcb6e);
          color: #d63031;
        `;
      case 'confirmed':
        return `
          background: linear-gradient(135deg, #a8e6cf, #55a3ff);
          color: #00b894;
        `;
      case 'completed':
        return `
          background: linear-gradient(135deg, #ddd6fe, #c084fc);
          color: #7c3aed;
        `;
      default:
        return `
          background: linear-gradient(135deg, #f1f2f6, #ddd6fe);
          color: #2d3436;
        `;
    }
  }}
`;

const AppointmentDetail = styled.div`
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DetailIcon = styled.div`
  color: #6c63ff;
  font-size: 1.1rem;
  width: 20px;
`;

const DetailText = styled.span`
  color: #2d3436;
  font-size: 1rem;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #636e72;
  font-weight: 400;
`;

const ZoomButton = styled.a`
  background: linear-gradient(135deg, #2d8cff, #0066cc);
  color: white;
  padding: 0.75rem 1.5rem;
  text-decoration: none;
  border-radius: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(45, 140, 255, 0.3);
    text-decoration: none;
    color: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #636e72;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 1.1rem;
  margin: 0;
`;

const ClientAppointment = () => {
  const [message, setMessage] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = auth.currentUser;

  const getPartnerIdFromSubcollection = async (customerId) => {
    const q = collectionGroup(db, 'clients');
    const snapshot = await getDocs(q);

    for (let docSnap of snapshot.docs) {
      if (docSnap.id === customerId) {
        const path = docSnap.ref.path;
        return path.split('/')[1];
      }
    }
    throw new Error('Partner not found for this user.');
  };

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'appointments'), where('customerId', '==', user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort by timestamp, newest first
      data.sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return b.timestamp.seconds - a.timestamp.seconds;
        }
        return 0;
      });
      setAppointments(data);
    });
    return () => unsub();
  }, [user]);

  const requestAppointment = async () => {
    if (!message.trim()) {
      alert("Please enter a message before sending your request.");
      return;
    }
    
    setLoading(true);
    try {
      const partnerId = await getPartnerIdFromSubcollection(user.uid);
      await addDoc(collection(db, 'appointments'), {
        customerId: user.uid,
        partnerId,
        status: 'pending',
        message,
        timestamp: serverTimestamp()
      });
      setMessage('');
      alert("Appointment request sent successfully!");
    } catch (err) {
      alert("Failed to send request: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatZoomLink = (link) => {
    // Ensure the link opens in Zoom app or web
    if (link && link.includes('zoom.us')) {
      return link;
    }
    return link;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not set';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Not set';
    return new Date(timestamp.seconds * 1000).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container>
      <Header>
        <MainTitle>Client Appointments</MainTitle>
        <Subtitle>Schedule and manage your appointments with your partner</Subtitle>
      </Header>

      <Card>
        <SectionTitle>
          <FaComments />
          Request New Appointment
        </SectionTitle>
        <FormGroup>
          <Label htmlFor="message">Message to your partner</Label>
          <TextArea
            id="message"
            placeholder="Describe your appointment needs, preferred dates, or any specific requirements..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
        </FormGroup>
        <Button onClick={requestAppointment} disabled={loading}>
          <FaPaperPlane />
          {loading ? 'Sending...' : 'Send Request'}
        </Button>
      </Card>

      <Card>
        <SectionTitle>
          <FaCalendarAlt />
          My Appointments ({appointments.length})
        </SectionTitle>
        
        {appointments.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <FaCalendarAlt />
            </EmptyIcon>
            <EmptyText>No appointments scheduled yet.</EmptyText>
          </EmptyState>
        ) : (
          <AppointmentsGrid>
            {appointments.map(app => (
              <AppointmentCard key={app.id}>
                <StatusBadge status={app.status}>{app.status}</StatusBadge>
                
                <AppointmentDetail>
                  <DetailIcon><FaUser /></DetailIcon>
                  <DetailText>Status:</DetailText>
                  <DetailValue>{app.status}</DetailValue>
                </AppointmentDetail>

                <AppointmentDetail>
                  <DetailIcon><FaCalendarAlt /></DetailIcon>
                  <DetailText>Date:</DetailText>
                  <DetailValue>{app.date || formatDate(app.timestamp)}</DetailValue>
                </AppointmentDetail>

                <AppointmentDetail>
                  <DetailIcon><FaClock /></DetailIcon>
                  <DetailText>Time:</DetailText>
                  <DetailValue>{app.time || formatTime(app.timestamp)}</DetailValue>
                </AppointmentDetail>

                {app.message && (
                  <AppointmentDetail>
                    <DetailIcon><FaComments /></DetailIcon>
                    <DetailText>Message:</DetailText>
                    <DetailValue>{app.message}</DetailValue>
                  </AppointmentDetail>
                )}

                {app.meetingLink && (
                  <ZoomButton 
                    href={formatZoomLink(app.meetingLink)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <FaVideo />
                    Join Zoom Meeting
                    <FaExternalLinkAlt />
                  </ZoomButton>
                )}
              </AppointmentCard>
            ))}
          </AppointmentsGrid>
        )}
      </Card>
    </Container>
  );
};

export default ClientAppointment;